import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  ContributorEmployment,
  ContributorEmploymentEditResource,
  ContributorEmploymentViewResource,
  Permission,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ContributorEmploymentPageBody } from "./ContributorEmploymentPageBody";
import { ContributorEmploymentPageHeader } from "./ContributorEmploymentPageHeader";

const routeName = "contributorEmployment";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  contributorId?: number = 0;
}

export const contributorEmploymentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Employment",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const ContributorEmploymentPage = () => {
  const ROOT_PERMISSION: Permission = Permission.ContributorEmployment;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [employment, setEmployment] = useState<ContributorEmployment>({} as ContributorEmployment);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [employment] });
  const [viewResource, setViewResource] = useState<ContributorEmploymentViewResource>({});
  const [editResource, setEditResource] = useState<ContributorEmploymentEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.contributorEmployments.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit")
      ApiService.contributorEmployments.getEditResource().then((response) => {
        setEditResource(response);
      });
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${contributorEmploymentPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.contributorEmployments.get(id).then((response) => {
      if (id === 0 && optionalParams.contributorId) response.contributorId = optionalParams.contributorId;

      setEmployment(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <ContributorEmploymentPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={contributorEmploymentPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}      
      fetch={fetch}
      
      employment={employment}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <ContributorEmploymentPageBody
      crudMode={params.crudMode}
      employment={employment}
      setEmployment={setEmployment}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
